/*
  Shipping calculator inside the cart drawer.

  WHY THIS IS A SEPARATE FILE, AND MUST STAY ONE
  ----------------------------------------------
  This logic used to live in an inline <script> inside snippets/cart-drawer.liquid.
  The drawer re-renders itself with `sectionElement.innerHTML = html` (see
  renderContents in assets/cart-drawer.js), and **scripts inserted via innerHTML are
  never executed by the browser**. So the moment anything changed the cart - adding a
  product, changing a quantity, or the product-page calculator restoring the cart -
  the Calculate button lost its only click handler. Clicking it did nothing at all:
  no rates, no error, not even the "Calculating..." line, because the code that would
  have written that line no longer existed on the page.

  The handler is therefore delegated from `document`, which survives any number of
  re-renders, and lives in a file the browser actually loads.

  Unlike the product-page calculator, this one quotes the WHOLE cart on purpose -
  that is the correct number here, and it reads rates without mutating the cart.
*/
(function () {
  'use strict';

  var BTN = '#ShippingCalculator-Submit';
  var OPEN_KEY = 'ctxCartShipCalcModalOpen';

  /*
    ─────────────────────────────────────────────────────────────────────────────
    POPOUT MODAL — replaced the inline <details> disclosure, 9 Aug 2026.

    Dillon wanted the calculator reachable without scrolling, same as Checkout
    and the 30-Day Returns line, and an inline disclosure could not do that no
    matter where it sat — it was still part of the scrolling list. The trigger
    now lives in the static footer; the real calculator lives in
    #CartDrawerShipCalcModal, a fixed-position overlay contained to the drawer
    panel (see the markup comment in cart-drawer.liquid for why it stays inside
    the drawer's box rather than covering the whole page - a transform on
    .drawer__inner does that for free, no viewport-pinning JS needed).

    The state-loss problem this already had to solve for <details> is UNCHANGED
    by becoming a modal: the drawer re-renders its entire section's innerHTML on
    every cart change, so whatever the customer had open gets discarded and
    rebuilt from scratch, with the modal defaulting back to hidden. Same fix as
    before - remember it in sessionStorage, re-apply after every mutation.
    ─────────────────────────────────────────────────────────────────────────────
  */
  function isOpen() {
    try { return sessionStorage.getItem(OPEN_KEY) === '1'; } catch (e) { return false; }
  }

  function setOpen(open) {
    try { sessionStorage.setItem(OPEN_KEY, open ? '1' : '0'); } catch (e) {}
  }

  function apply(modal, open) {
    if (open) modal.removeAttribute('hidden');
    else modal.setAttribute('hidden', '');
    var trigger = document.querySelector('[data-cart-open-calc]');
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function getModal() {
    return document.getElementById('CartDrawerShipCalcModal');
  }

  /* Re-apply the remembered state to whatever markup is on the page now. Cheap
     enough to run on any drawer mutation. */
  function syncModal() {
    var modal = getModal();
    if (!modal) return;
    var want = isOpen();
    if (modal.hasAttribute('hidden') === want) apply(modal, want);
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-cart-open-calc]')) {
      var modal = getModal();
      if (!modal) return;
      setOpen(true);
      apply(modal, true);
      var firstField = modal.querySelector('select, input');
      if (firstField && !window.matchMedia('(pointer: coarse)').matches) {
        setTimeout(function () { firstField.focus(); }, 60);
      }
      return;
    }
    if (e.target.closest && e.target.closest('[data-cart-close-calc]')) {
      var m = getModal();
      if (!m) return;
      setOpen(false);
      apply(m, false);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var modal = getModal();
    if (modal && !modal.hasAttribute('hidden')) { setOpen(false); apply(modal, false); }
  });

  /* The drawer replaces its markup wholesale; watch for it and restore the state. */
  function watchForRerenders() {
    var host = document.querySelector('cart-drawer') || document.body;
    if (!host || host.dataset.ctxCalcWatched) return;
    host.dataset.ctxCalcWatched = '1';
    new MutationObserver(function () { syncModal(); }).observe(host, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { syncModal(); watchForRerenders(); });
  } else {
    syncModal();
    watchForRerenders();
  }

  function money(v) {
    var n = parseFloat(v);
    if (!isFinite(n)) return v;
    return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* Flag the list only when it really overflows - the fade is the "more below" cue
     and must not appear on a complete list. Mirrors markScrollable() in
     ctx-shipping-calc.js. */
  function markScrollable(box) {
    if (!box) return;
    var check = function () {
      box.classList.toggle('is-scrollable', box.scrollHeight - box.clientHeight > 2);
    };
    if (window.requestAnimationFrame) window.requestAnimationFrame(check);
    else check();
    window.addEventListener('resize', check);
    if (window.ResizeObserver) new ResizeObserver(check).observe(box);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest(BTN) : null;
    if (!btn) return;

    /* Scoped to the calculator this button belongs to rather than looked up by id,
       so a second calculator on the page cannot write into the first one's results. */
    var root = btn.closest('.shipping-calculator') || document;
    var out = root.querySelector('#ShippingCalculator-Results, .shipping-calculator__message');
    var sel = root.querySelector('#ShippingCalculator-Country, .shipping-calculator__select');
    var zipEl = root.querySelector('#ShippingCalculator-Zip, .shipping-calculator__input');
    if (!out || !sel || !zipEl) return;

    /* The state list stores values as US-TX. Shopify's endpoint wants the province
       on its own, so drop the country prefix. */
    var province = (sel.value || '').replace(/^US-/, '').trim();
    var zip = (zipEl.value || '').trim();

    if (!province || !zip) {
      out.textContent = 'Pick a state and enter your ZIP code.';
      return;
    }

    out.textContent = 'Calculating…';
    btn.disabled = true;

    fetch('/cart/shipping_rates.json'
        + '?shipping_address[zip]=' + encodeURIComponent(zip)
        + '&shipping_address[country]=US'
        + '&shipping_address[province]=' + encodeURIComponent(province))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        btn.disabled = false;
        var rates = data && data.shipping_rates;
        if (!rates || !rates.length) {
          out.textContent = 'No rates available for that address. Call us and we will quote it.';
          return;
        }

        /* Cheapest first. Sorted on a copy, and with parseFloat because price is a
           string - as text, "150.00" sorts before "49.00". */
        var sorted = rates.slice().sort(function (a, b) {
          return parseFloat(a.price) - parseFloat(b.price);
        });

        var rows = '';
        sorted.forEach(function (r) {
          var note = window.ctxRateNote ? window.ctxRateNote(r.name) : '';
          rows += '<span class="ctx-rate-row"><span>' + r.name + '</span><strong>'
                + (parseFloat(r.price) === 0 ? 'Free' : money(r.price))
                + '</strong></span>';
          /* Says what the price covers - assembly is a rate now, not a product
             option, so this is the only place it gets explained. */
          if (note) rows += '<span class="ctx-rate-what">' + note + '</span>';
        });

        /* Fixed-height scroller so a long list cannot push the drawer's Checkout
           button out of reach. The footer note stays outside it. */
        out.innerHTML =
          '<span class="ctx-rates" tabindex="0" role="group" aria-label="Shipping rates">' + rows + '</span>' +
          '<span class="ctx-rate-note">Estimate. Final rate is confirmed at checkout.</span>';

        markScrollable(out.querySelector('.ctx-rates'));
      })
      .catch(function () {
        btn.disabled = false;
        out.textContent = 'Could not reach the rate service. Try again in a moment.';
      });
  });
})();
